import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { AmendClientMarketOrderMutationRequest, AmendClientMarketOrderMutationResponse, AmendClientMarketOrderPathParams, AmendClientMarketOrder404 } from "../types/AmendClientMarketOrder";
import type { UseMutationOptions } from "@tanstack/react-query";

 type AmendClientMarketOrderClient = typeof client<AmendClientMarketOrderMutationResponse, AmendClientMarketOrder404, AmendClientMarketOrderMutationRequest>;
type AmendClientMarketOrder = {
    data: AmendClientMarketOrderMutationResponse;
    error: AmendClientMarketOrder404;
    request: AmendClientMarketOrderMutationRequest;
    pathParams: AmendClientMarketOrderPathParams;
    queryParams: never;
    headerParams: never;
    response: AmendClientMarketOrderMutationResponse;
    client: {
        parameters: Partial<Parameters<AmendClientMarketOrderClient>[0]>;
        return: Awaited<ReturnType<AmendClientMarketOrderClient>>;
    };
};
/**
 * @description Amends an existing order for a given client market. This route requires the
original client order reference to be provided plus a new client order
reference, which must be unique for the market on the same day. Each time you
amend an order you would need to provide the altered client order reference
and another new client order reference.
 * @summary Amend
 * @link /api/clients/:clientCode/markets/:marketCode/orders/:originalClientOrderRef/:clientOrderRef
 */
export function useAmendClientMarketOrder(clientCode: AmendClientMarketOrderPathParams["clientCode"], marketCode: AmendClientMarketOrderPathParams["marketCode"], originalClientOrderRef: AmendClientMarketOrderPathParams["originalClientOrderRef"], clientOrderRef: AmendClientMarketOrderPathParams["clientOrderRef"], options: {
    mutation?: UseMutationOptions<AmendClientMarketOrder["response"], AmendClientMarketOrder["error"], AmendClientMarketOrder["request"]>;
    client?: AmendClientMarketOrder["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async (data) => {
            const res = await client<AmendClientMarketOrder["data"], AmendClientMarketOrder["error"], AmendClientMarketOrder["request"]>({
                method: "put",
                url: `/api/clients/${clientCode}/markets/${marketCode}/orders/${originalClientOrderRef}/${clientOrderRef}`,
                data,
                headers: { "Content-Type": "text/json", ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}